// search.repo — pencarian GLOBAL servisan (RLS-scoped: user hanya temukan tiket dalam scope-nya).
// 2 query (tiket-fields + via customer) di-merge & dedup by `no`. ILIKE cukup utk skala toko.
import { supabase, pat } from '@core/supabase';

export interface SearchHit { no: string; status: string; custNama: string; unit: string; teknisi: string | null }

type Raw = { no: string; status: string; unit: string | null; teknisi: string | null; customers: { nama: string } | { nama: string }[] | null };
const SEL = 'no,status,unit,teknisi,customers(nama)';


const toHit = (r: Raw): SearchHit => {
  const c = Array.isArray(r.customers) ? r.customers[0] : r.customers;
  return { no: r.no, status: r.status, custNama: c?.nama ?? '—', unit: r.unit ?? '', teknisi: r.teknisi ?? null };
};

// ---------- Sparepart (CS cari barang langsung dari kotak pencarian) ----------
export interface PartHit { id: number; nama: string; barcode: string; kategori: string; stok: number; harga: number; supplier: string; outlet: string | null; luarCabang: boolean }

/**
 * Apakah part ini dari CABANG LAIN (relatif ke pemakai)? Dipakai untuk URUTAN + penanda, BUKAN untuk
 * menyembunyikan: `requestDialog` (sparepart.view) menawarkan SEMUA supplier aktif lintas cabang, jadi
 * menyembunyikan part cabang lain akan menjawab "barang tak ada" padahal sah dipesan (kehilangan penjualan).
 * Cabang melekat di suppliers.outlet (tabel spareparts tak punya kolom outlet); outlet null = melayani semua.
 * Owner/Manager tak ditandai (memang lihat semua cabang).
 */
export const spLuarCabang = (partOutlet: string | null, role: string, myOutlet: string | null): boolean =>
  !(role === 'Owner' || role === 'Manager' || partOutlet == null || partOutlet === myOutlet);

type PartRaw = { id: number; nama: string; barcode: string | null; kategori: string | null; stok: number | null; harga: number | null;
  suppliers: { nama: string; outlet: string | null; aktif: boolean | null } | { nama: string; outlet: string | null; aktif: boolean | null }[] | null };

/**
 * Cari sparepart by nama·barcode → CS bisa cek ketersediaan & harga langsung dari topbar.
 * HARGA = harga beli supplier (modal). Keputusan Apo 18 Jul 2026: TAMPIL untuk SEMUA peran
 * (termasuk CS & Teknisi) — pengecualian sadar atas canSeeModal, jangan "diperbaiki" balik.
 * ponytail: scope cabang disaring di klien (ambil 40 lalu potong 15) — spareparts tak punya kolom
 * outlet & filter tabel-join PostgREST rapuh; skala toko masih kecil. Pindah ke server bila katalog membesar.
 */
export async function searchPartsGlobal(term: string, role: string, myOutlet: string | null): Promise<PartHit[]> {
  const t = term.trim();
  if (t.length < 2) return [];
  const p = pat(t);
  const { data, error } = await supabase().from('spareparts')
    .select('id,nama,barcode,kategori,stok,harga,suppliers(nama,outlet,aktif)')
    .or(`nama.ilike.${p},barcode.ilike.${p}`)
    .order('nama').limit(40);
  if (error) throw new Error(error.message);
  const out: PartHit[] = [];
  for (const r of (data ?? []) as unknown as PartRaw[]) {
    const s = Array.isArray(r.suppliers) ? r.suppliers[0] : r.suppliers;
    if (!s || s.aktif === false) continue; // supplier hilang/nonaktif → jangan tawarkan (fail-closed)
    out.push({ id: r.id, nama: r.nama, barcode: r.barcode ?? '', kategori: r.kategori ?? '', stok: r.stok ?? 0, harga: r.harga ?? 0,
      supplier: s.nama, outlet: s.outlet, luarCabang: spLuarCabang(s.outlet, role, myOutlet) });
  }
  // Cabang sendiri dulu, lalu stok ada dulu — yang paling siap dipakai muncul di atas.
  out.sort((a, b) => (+a.luarCabang - +b.luarCabang) || (+(b.stok > 0) - +(a.stok > 0)) || a.nama.localeCompare(b.nama));
  return out.slice(0, 15);
}

/** Cari servisan by no·token·SN·unit·merk·model (tiket) + nama·WA (pelanggan). Exclude arsip (via menu Arsip). */
export async function searchTickets(term: string): Promise<SearchHit[]> {
  const t = term.trim();
  if (t.length < 2) return [];
  const p = pat(t);
  const sb = supabase();
  const qTicket = sb.from('tickets').select(SEL).eq('archived', false)
    .or(`no.ilike.${p},token.ilike.${p},sn.ilike.${p},unit.ilike.${p},merk.ilike.${p},model.ilike.${p}`)
    .limit(15);
  const qViaCust = (async (): Promise<Raw[]> => {
    const { data: cs } = await sb.from('customers').select('id').or(`nama.ilike.${p},wa.ilike.${p}`).limit(20);
    const ids = ((cs ?? []) as { id: number }[]).map((c) => c.id);
    if (!ids.length) return [];
    const { data } = await sb.from('tickets').select(SEL).eq('archived', false).in('customer_id', ids).limit(15);
    return (data ?? []) as unknown as Raw[];
  })();
  const [ra, viaCust] = await Promise.all([qTicket, qViaCust]);
  if (ra.error) throw new Error(ra.error.message);
  const rows = [...((ra.data ?? []) as unknown as Raw[]), ...viaCust];
  const seen = new Set<string>();
  const out: SearchHit[] = [];
  for (const r of rows) { if (seen.has(r.no)) continue; seen.add(r.no); out.push(toHit(r)); }
  return out.slice(0, 15);
}
